import { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import { OpenNewWindow } from 'iconoir-react'
import AttributionCard from 'app/components/analytics/AttributionCard'
import AttributionFlow from 'app/components/analytics/AttributionFlow'
import AttributionGraph from 'app/components/analytics/AttributionGraph'
import DashboardHeader from 'app/components/analytics/DashboardHeader'
import DataCleanupNotice from 'app/components/analytics/DataCleanupNotice'
import ProductionByChannel from 'app/components/analytics/ProductionByChannel'
import { SectionHeader } from 'app/components/analytics/SectionHeader'
import { PageScroll } from 'app/components/layout/PageScroll'
import { AttributionTable } from 'app/components/shared/AttributionTable'
import Spinner from 'app/components/Spinner'
import { UseAttributionReturn } from './useAttribution'

export type AttributionShadcnComponentProps = UseAttributionReturn

export function AttributionShadcnComponent({
  isV2Enabled,
  fromDate,
  toDate,
  locationIds,
  integrationStatus,
  lifecycleFilter,
  setLifecycleFilter,
  summary,
  days,
  sourceRows,
  mediumRows,
  sourceMediumRows,
  campaignRows,
  adCampaignRows,
  adPlatformRows,
  channelRows,
  reportedAttributionRows,
  isLoading,
  dateChangeHandler,
}: AttributionShadcnComponentProps): ReactElement {
  return (
    <PageScroll
      contentClassName="flex flex-col items-center pb-40"
      header={
        <DashboardHeader
          title="Attribution"
          dateChangeHandler={dateChangeHandler}
          limitDateRangeToMonth={false}
          hideCompare={true}
          defaultDateRange={`this_month`}
          allowFutureDates={false}
          hideChannelSelector={true}
          extraButtons={
            <>
              <Link
                to="/settings/mapping"
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-tertiary flex items-center px-4 py-2"
              >
                <p>Adjust Mappings</p>
                <OpenNewWindow className="ml-2 h-4 w-4" />
              </Link>
            </>
          }
        />
      }
    >
      {!summary ? (
        <div className="flex w-full grow items-center justify-center">
          <Spinner size={20} />
        </div>
      ) : (
        <>
          {isLoading && (
            <div className="fixed inset-x-0 top-0 z-50 flex justify-center pt-2">
              <Spinner size={16} />
            </div>
          )}
          <DataCleanupNotice sourceRows={sourceRows} mediumRows={mediumRows} sourceMediumRows={sourceMediumRows} />
          {isV2Enabled ? (
            <div className="mt-8 flex w-full max-w-[1680px] gap-6">
              <div className="w-full">
                <AttributionFlow
                  channelRows={channelRows}
                  sourceRows={sourceRows}
                  mediumRows={mediumRows}
                  campaignRows={campaignRows}
                  summary={summary}
                  fromDate={fromDate}
                  toDate={toDate}
                  locationIds={locationIds}
                  integrationStatus={integrationStatus}
                />
              </div>
            </div>
          ) : (
            <div className="mt-8 flex w-full max-w-[1680px] flex-wrap gap-6">
              <AttributionCard
                type="sources"
                data={summary}
                fromDate={fromDate}
                toDate={toDate}
                locationIds={locationIds}
                integrationStatus={integrationStatus}
              />
              <AttributionCard
                type="mediums"
                data={summary}
                fromDate={fromDate}
                toDate={toDate}
                locationIds={locationIds}
                integrationStatus={integrationStatus}
              />
              <AttributionCard
                type="campaigns"
                data={summary}
                fromDate={fromDate}
                toDate={toDate}
                locationIds={locationIds}
                integrationStatus={integrationStatus}
              />
            </div>
          )}
          {isV2Enabled && (
            <div className="mt-8 w-full max-w-[1680px]">
              <SectionHeader title="Channels" description="Channels are groups of Source and Medium Pairings." />
            </div>
          )}
          <div className="border-default mt-7 w-full max-w-[1680px] rounded-lg border p-5">
            <AttributionGraph
              dayMetrics={days}
              channelRows={channelRows}
              isLoading={isLoading}
              filter={lifecycleFilter}
              onFilterChange={setLifecycleFilter}
            />
          </div>
          {isV2Enabled && (
            <>
              <div className="mt-8 flex w-full max-w-[1680px] gap-6">
                <div className="w-full">
                  <ProductionByChannel channelRows={channelRows} />
                </div>
              </div>
              <div className="mt-7 w-full max-w-[1680px]">
                <SectionHeader title="Data Table" description="Adjust rows to show different slices of data. " />
              </div>
            </>
          )}
          <div className="border-default relative my-7 w-full max-w-[1680px] rounded-lg border p-5">
            {/* Overlay the toolbar's empty left area on desktop; on mobile the
                toolbar controls fill the width, so flow the title above them
                instead of letting it collide. */}
            <div className="relative z-30 mb-2 pt-1.5 text-heading-sm text-strong md:absolute md:mb-0">
              Table Breakdown
            </div>
            <div className="flex h-full w-full max-w-full flex-col overflow-hidden">
              <AttributionTable
                fromDate={fromDate}
                toDate={toDate}
                integrationStatus={integrationStatus}
                locationIds={locationIds}
                sourceRows={sourceRows}
                mediumRows={mediumRows}
                sourceMediumRows={sourceMediumRows}
                campaignRows={campaignRows}
                adCampaignRows={adCampaignRows}
                adPlatformRows={adPlatformRows}
                channelRows={channelRows}
                reportedAttributionRows={reportedAttributionRows}
                enableExpand={true}
              />
            </div>
          </div>
        </>
      )}
    </PageScroll>
  )
}
